import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { apiFetch } from "../api/client.ts";
import {
  createTechnician,
  listTechnicians,
  updateTechnician,
  type Technician,
  type TechnicianStatus,
} from "../api/technicians.ts";

const STATUSES: TechnicianStatus[] = ["available", "busy", "off_duty", "on_break"];

const STATUS_BADGE: Record<TechnicianStatus, string> = {
  available: "bg-emerald-100 text-emerald-700",
  busy: "bg-amber-100 text-amber-700",
  off_duty: "bg-slate-100 text-slate-700",
  on_break: "bg-blue-100 text-blue-700",
};

interface User {
  id: string;
  email: string;
  full_name: string;
  role: "admin" | "dispatcher" | "technician";
  is_active: boolean;
}

const listUsers = () => apiFetch<User[]>("/users");

export default function TechniciansPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["technicians"], queryFn: () => listTechnicians() });
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: listUsers });

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    user_id: "",
    skills: "",
    commission_pct: 0,
    home_base_address: "",
    timezone: "UTC",
  });

  // Eligible: technician-role users without a profile yet
  const eligibleUsers = useMemo(() => {
    if (!users || !data) return [];
    const taken = new Set(data.items.map((p) => p.user_id));
    return users.filter((u) => u.role === "technician" && u.is_active && !taken.has(u.id));
  }, [users, data]);

  const createMut = useMutation({
    mutationFn: () =>
      createTechnician({
        user_id: form.user_id,
        skills: form.skills ? form.skills.split(",").map((s) => s.trim()).filter(Boolean) : undefined,
        commission_pct: Number(form.commission_pct) || 0,
        home_base_address: form.home_base_address || undefined,
        timezone: form.timezone || "UTC",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["technicians"] });
      setCreating(false);
      setForm({ user_id: "", skills: "", commission_pct: 0, home_base_address: "", timezone: "UTC" });
    },
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TechnicianStatus }) => updateTechnician(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["technicians"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">{t("technicians.title")}</h1>
        <button type="button" className="btn-primary" onClick={() => setCreating((x) => !x)}>
          {creating ? t("common.cancel") : t("technicians.add")}
        </button>
      </div>

      {creating && (
        <form
          className="card grid gap-3 md:grid-cols-2"
          onSubmit={(e) => { e.preventDefault(); if (form.user_id) createMut.mutate(); }}
        >
          <label className="block text-sm md:col-span-2">
            <span className="mb-1 block text-slate-600">{t("technicians.form.user")}</span>
            <select
              className="input"
              value={form.user_id}
              onChange={(e) => setForm({ ...form, user_id: e.target.value })}
              required
            >
              <option value="" disabled>{t("technicians.form.userPlaceholder")}</option>
              {eligibleUsers.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>
              ))}
            </select>
            {eligibleUsers.length === 0 && (
              <p className="mt-1 text-xs text-slate-500">
                {t("technicians.form.noEligible")} <Link to="/users" className="text-brand-700 hover:underline">{t("technicians.form.goToUsers")}</Link>
              </p>
            )}
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">{t("technicians.form.skills")}</span>
            <input
              className="input"
              placeholder={t("technicians.form.skillsPlaceholder") ?? ""}
              value={form.skills}
              onChange={(e) => setForm({ ...form, skills: e.target.value })}
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">{t("technicians.form.commission")}</span>
            <input
              className="input"
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={form.commission_pct}
              onChange={(e) => setForm({ ...form, commission_pct: Number(e.target.value) })}
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">{t("technicians.form.homeBase")}</span>
            <input
              className="input"
              value={form.home_base_address}
              onChange={(e) => setForm({ ...form, home_base_address: e.target.value })}
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">{t("technicians.form.timezone")}</span>
            <input
              className="input"
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            />
          </label>

          <div className="md:col-span-2 flex items-center gap-2">
            <button type="submit" className="btn-primary" disabled={!form.user_id || createMut.isPending}>
              {t("technicians.add")}
            </button>
            {createMut.isError && (
              <span className="text-sm text-red-600">{(createMut.error as Error).message}</span>
            )}
          </div>
        </form>
      )}

      <div className="table-wrapper">
        <table className="table-base">
          <thead>
            <tr>
              <th className="px-4 py-2">{t("technicians.col.name")}</th>
              <th className="px-4 py-2">{t("technicians.col.email")}</th>
              <th className="px-4 py-2">{t("technicians.col.status")}</th>
              <th className="px-4 py-2">{t("technicians.col.skills")}</th>
              <th className="px-4 py-2">{t("technicians.col.commission")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>}
            {data?.items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">{t("technicians.empty")}</td></tr>}
            {data?.items.map((tech: Technician) => (
              <tr key={tech.id}>
                <td className="px-4 py-2 font-medium">{tech.user_full_name ?? "—"}</td>
                <td className="px-4 py-2 text-slate-600">{tech.user_email ?? "—"}</td>
                <td className="px-4 py-2">
                  <select
                    className={`rounded px-2 py-0.5 text-xs ${STATUS_BADGE[tech.status]}`}
                    value={tech.status}
                    onChange={(e) => statusMut.mutate({ id: tech.id, status: e.target.value as TechnicianStatus })}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{t(`technicians.status.${s}`)}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2 text-slate-600">{tech.skills?.join(", ") ?? "—"}</td>
                <td className="px-4 py-2 text-slate-600">{Number(tech.commission_pct).toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
